import {
  ShowBase,
  useNotify,
  useRefresh,
  useShowContext,
  useUpdate,
} from "ra-core";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  type CustomField,
  customDetailFields,
  type DetailField,
} from "./details";
import { formatCents } from "./format";
import { GraphDetails } from "./GraphDetails";
import { GraphError, GraphLoading } from "./GraphStatus";
import { FactDot, RecordHeader } from "./record/RecordHeader";
import { RecordCard, RecordRow } from "./record/RecordCard";
import { type ExternalId, ExternalIdsCard } from "./record/ExternalIdsCard";
import { type Assignment, TeamCard } from "./record/TeamCard";
import { MUTED, RECORD_LINK, roleText } from "./record/styles";
import { GraphTimeline } from "./GraphTimeline";
import { type GraphLink, GraphLinkList } from "./links";
import { GraphLinkEditor } from "./GraphLinkEditor";
import { AddPartyForm, EndPartyButton } from "./PartyEditor";
import { SignalBadge } from "./SignalBadge";
import { type Signal, signalDetail } from "./signals";
import { useSaveDetails } from "./useSaveDetails";
import { usePermission } from "../providers/usePermission";

/** One loan on a deal (0018, W15 M2): a first, a second, a HELOC. */
interface DealLoan {
  id: string;
  lien_position: string;
  lien_label: string;
  status: string;
  status_label: string;
  counts_toward_amount: boolean;
  loan_number: string | null;
  amount_cents: number | null;
  loan_program_label: string | null;
  rate: number | null;
}

/** Where one loan stands (0024, W15 M8). */
interface LoanProgress {
  loan_id: string;
  lien_position: string;
  open_conditions: number;
  cleared_conditions: number;
  next_milestone_label: string | null;
  next_due_on: string | null;
  off_track_reason: string | null;
  conditions: {
    id: string;
    category_label: string;
    description: string;
    status: string;
  }[];
  milestones: {
    milestone: string;
    label: string;
    due_on: string | null;
    done_at: string | null;
    is_late: boolean;
  }[];
}

interface GraphDeal {
  id: string;
  signals?: Signal[];
  branch_id?: string | null;
  branch_name?: string | null;
  team_id?: string | null;
  team_name?: string | null;
  name: string;
  /** The first loan's amount, the one Details edits (0018, W15 M2). */
  amount_cents: number | null;
  /** All the deal's loans that still count. */
  total_amount_cents?: number | null;
  loans?: DealLoan[];
  loan_progress?: LoanProgress[];
  pipeline_name: string;
  /** `loan`, `title`, `insurance`, `recruiting`; null when unset (0011). */
  pipeline_kind?: string | null;
  stage_id: string;
  stage_label: string;
  parties: {
    id: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    role: string;
    is_primary: boolean;
  }[];
  stages: { id: string; label: string; sort_index: number }[];
  // Mortgage facts (W8 D5).
  loan_program?: string | null;
  rate?: number | null;
  estimated_close_on?: string | null;
  closed_on?: string | null;
  property_line1?: string | null;
  property_city?: string | null;
  property_state?: string | null;
  property_postal_code?: string | null;
  occupancy?: string | null;
  fact_choices?: Record<
    "loan_program" | "occupancy",
    { id: string; label: string }[]
  >;
  custom_fields?: CustomField[];
  links?: GraphLink[];
  assignments?: Assignment[];
  external_ids?: ExternalId[];
}

const asChoices = (rows: { id: string; label: string }[] = []) =>
  rows.map((row) => ({ value: row.id, label: row.label }));

/** Mortgage facts belong to loans; a pipeline with no kind counts as one. */
const isLoan = (deal: GraphDeal) =>
  deal.pipeline_kind == null || deal.pipeline_kind === "loan";

/** A loan's mortgage facts, then the tenant's custom deal fields. */
function dealDetailFields(deal: GraphDeal): DetailField[] {
  const custom = customDetailFields(deal.custom_fields ?? []);
  // Every deal has a name (W10 H3).
  const nameField: DetailField = {
    key: "name",
    label: "Name",
    type: "text",
    value: deal.name,
  };
  if (!isLoan(deal)) return [nameField, ...custom];
  const choices = deal.fact_choices;
  const text = (key: keyof GraphDeal, label: string): DetailField => ({
    key,
    label,
    type: "text",
    value: (deal[key] as string | null | undefined) ?? null,
  });
  return [
    nameField,
    {
      // Edited in dollars; DealDetails sends cents.
      key: "amount_cents",
      label: "Loan amount ($)",
      type: "number",
      value: deal.amount_cents == null ? null : deal.amount_cents / 100,
      format: (dollars) => formatCents(Number(dollars) * 100),
    },
    {
      key: "loan_program",
      label: "Loan program",
      type: "choice",
      choices: asChoices(choices?.loan_program),
      value: deal.loan_program ?? null,
    },
    {
      key: "rate",
      label: "Rate (%)",
      type: "number",
      value: deal.rate ?? null,
      format: (rate) => `${rate}%`,
    },
    {
      key: "estimated_close_on",
      label: "Estimated close",
      type: "date",
      value: deal.estimated_close_on ?? null,
    },
    {
      key: "closed_on",
      label: "Closed",
      type: "date",
      value: deal.closed_on ?? null,
    },
    {
      key: "occupancy",
      label: "Occupancy",
      type: "choice",
      choices: asChoices(choices?.occupancy),
      value: deal.occupancy ?? null,
    },
    text("property_line1", "Property address"),
    text("property_city", "City"),
    text("property_state", "State"),
    text("property_postal_code", "ZIP"),
    ...custom,
  ];
}

/** Title, insurance and other deals linked to this one, either direction. */
/**
 * Where each loan stands (W15 M8): off track and why, conditions cleared, the
 * next milestone, and the open conditions. Read-only; Encompass fills these
 * later.
 */
function LoanProgressCard({
  progress,
  loans,
}: {
  progress: LoanProgress[];
  loans: DealLoan[];
}) {
  const shown = progress.filter(
    (p) => p.milestones.length || p.conditions.length || p.off_track_reason,
  );
  if (!shown.length) return null;
  const lienLabel = (id: string) =>
    loans.find((l) => l.id === id)?.lien_label ?? "Loan";
  return (
    <RecordCard title="Loan progress">
      <div className="space-y-4">
        {shown.map((p) => {
          const total = p.open_conditions + p.cleared_conditions;
          const open = p.conditions.filter(
            (c) => c.status === "open" || c.status === "submitted",
          );
          return (
            <div key={p.loan_id} className="space-y-1.5 text-sm">
              {shown.length > 1 ? (
                <div className="font-medium">{lienLabel(p.loan_id)}</div>
              ) : null}
              {p.off_track_reason ? (
                <div className="rounded bg-destructive/10 px-2 py-1 text-destructive">
                  Off track · {p.off_track_reason}
                </div>
              ) : null}
              {total ? (
                <div>
                  {p.cleared_conditions} of {total} conditions cleared
                </div>
              ) : null}
              {p.next_milestone_label ? (
                <div className={MUTED}>
                  Next: {p.next_milestone_label}
                  {p.next_due_on ? ` · due ${p.next_due_on}` : ""}
                </div>
              ) : null}
              {open.length ? (
                <ul className="list-disc pl-5 text-muted-foreground">
                  {open.map((c) => (
                    <li key={c.id}>
                      {c.description}{" "}
                      <span className="text-xs">({c.category_label})</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </RecordCard>
  );
}

/** The deal's loans, first lien first (W15 M2). Read-only for now. */
function LoansCard({ loans }: { loans: DealLoan[] }) {
  if (!loans.length) return null;
  return (
    <RecordCard title="Loans" count={loans.length}>
      <ul>
        {loans.map((loan) => (
          <RecordRow
            key={loan.id}
            primary={
              <>
                <span className="font-medium">{loan.lien_label}</span>
                {loan.amount_cents != null ? (
                  <span> · {formatCents(loan.amount_cents)}</span>
                ) : null}
                {loan.counts_toward_amount ? null : (
                  <span className={MUTED}> (not counted)</span>
                )}
              </>
            }
            secondary={[
              loan.loan_program_label,
              loan.rate != null ? `${loan.rate}%` : null,
              loan.status_label,
              loan.loan_number ? `#${loan.loan_number}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          />
        ))}
      </ul>
    </RecordCard>
  );
}

function RelatedDeals({
  dealId,
  links,
}: {
  dealId: string;
  links: GraphLink[];
}) {
  const deals = links.filter((link) => link.other_type === "deal");
  const canLink = usePermission("links");
  return (
    <RecordCard title="Related deals" count={deals.length}>
      {deals.length ? (
        <GraphLinkList links={deals} canEnd={canLink} showType />
      ) : (
        <p className="text-sm text-muted-foreground">None</p>
      )}
      {canLink ? <GraphLinkEditor objectType="deal" objectId={dealId} /> : null}
    </RecordCard>
  );
}

function DealDetails({ deal }: { deal: GraphDeal }) {
  const fields = dealDetailFields(deal);
  const { save, isSaving } = useSaveDetails("deals", deal.id, fields);
  const canEdit = usePermission("edit_details");
  if (!fields.length) return null;
  return (
    <div className="rounded-xl border bg-card px-5 py-4 shadow-xs">
      <GraphDetails
        title="Details"
        fields={fields}
        onSave={(changes) =>
          save(
            "amount_cents" in changes
              ? {
                  ...changes,
                  amount_cents:
                    changes.amount_cents == null
                      ? null
                      : Math.round(Number(changes.amount_cents) * 100),
                }
              : changes,
          )
        }
        isSaving={isSaving}
        canEdit={canEdit}
      />
    </div>
  );
}

export function GraphDealShow() {
  return (
    <ShowBase queryOptions={{ onError: () => undefined }}>
      <DealGraph />
    </ShowBase>
  );
}

/**
 * Where the deal is in its pipeline (W12 U3): every stage in order, those
 * passed filled grey, the current one solid. Roles that may move stages click
 * one to move there; others just see the path.
 */
function StagePath({
  deal,
  canMove,
  isSaving,
  onMove,
}: {
  deal: GraphDeal;
  canMove: boolean;
  isSaving: boolean;
  onMove: (stage: { id: string; label: string }) => void;
}) {
  const stages = deal.stages ?? [];
  const current = stages.findIndex((s) => s.id === deal.stage_id);
  return (
    <section className="border-t pt-4">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {canMove ? "Move stage" : "Stage"}
      </h2>
      <div className="flex flex-wrap gap-1">
        {stages.map((stage, index) => {
          const isCurrent = index === current;
          const isDone = current >= 0 && index < current;
          const look = cn(
            "h-8 rounded-md border px-3 text-xs font-medium transition-colors",
            isCurrent && "border-primary bg-primary text-primary-foreground",
            isDone && "border-transparent bg-muted text-foreground",
            !isCurrent && !isDone && "bg-background text-muted-foreground",
          );
          return canMove ? (
            <button
              key={stage.id}
              type="button"
              className={cn(
                look,
                !isCurrent &&
                  "hover:border-foreground/40 hover:text-foreground",
                "disabled:cursor-default",
              )}
              aria-current={isCurrent ? "step" : undefined}
              disabled={isSaving || isCurrent}
              onClick={() => onMove(stage)}
            >
              {stage.label}
            </button>
          ) : (
            <span
              key={stage.id}
              className={cn(look, "inline-flex items-center")}
              aria-current={isCurrent ? "step" : undefined}
            >
              {stage.label}
            </span>
          );
        })}
      </div>
    </section>
  );
}

/**
 * A deal (W12 U3): header with the stage path, then details on the left, the
 * activity in the middle, and parties and related deals on the right.
 */
function DealGraph() {
  const { record, isPending, error } = useShowContext<GraphDeal>();
  const [update, { isPending: isSaving }] = useUpdate();
  const refresh = useRefresh();
  const notify = useNotify();
  const canMoveStage = usePermission("move_stage");
  const canEditParties = usePermission("edit_details");
  if (isPending) return <GraphLoading shape="record" />;
  if (error || !record) return <GraphError error={error} noun="deal" />;

  const move = (stage: { id: string; label: string }) =>
    update(
      "deals",
      {
        id: record.id,
        data: { stage_id: stage.id, stage_label: stage.label },
        previousData: record,
      },
      {
        onSuccess: () => refresh(),
        // Pessimistic update: nothing moved on screen, so reload the deal and
        // say why the move did not happen.
        onError: () => {
          notify("The stage could not be changed", { type: "error" });
          refresh();
        },
      },
    );

  return (
    <div className="space-y-6">
      <RecordHeader
        name={record.name}
        shape="square"
        chips={(record.signals ?? []).map((signal) => (
          <SignalBadge key={signal.id} typeId={signal.type_id} />
        ))}
        facts={
          <>
            <span>
              {record.pipeline_name} · {record.stage_label}
            </span>
            {record.total_amount_cents != null ? (
              <>
                <FactDot />
                <span>
                  {(record.loans?.length ?? 0) > 1
                    ? `${record.loans?.length} loans, ${formatCents(record.total_amount_cents)}`
                    : `loan amount ${formatCents(record.total_amount_cents)}`}
                </span>
              </>
            ) : null}
            {record.branch_id ? (
              <>
                <FactDot />
                <Link
                  className={RECORD_LINK + " font-normal text-muted-foreground"}
                  to={`/companies/${record.branch_id}/show`}
                >
                  {record.branch_name}
                </Link>
                {record.team_id ? (
                  <>
                    <FactDot />
                    <Link
                      className={
                        RECORD_LINK + " font-normal text-muted-foreground"
                      }
                      to={`/companies/${record.team_id}/show`}
                    >
                      {record.team_name}
                    </Link>
                  </>
                ) : null}
              </>
            ) : null}
          </>
        }
        actions={
          record.pipeline_name?.toLowerCase().includes("process") ? (
            <Button variant="secondary" disabled>
              Issue pre-approval
            </Button>
          ) : null
        }
      >
        {(record.signals ?? []).map((signal) => (
          <p key={signal.id} className="text-sm text-muted-foreground">
            {signalDetail(signal)}
          </p>
        ))}
        <StagePath
          deal={record}
          canMove={canMoveStage}
          isSaving={isSaving}
          onMove={move}
        />
      </RecordHeader>

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_minmax(0,22rem)] 2xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)_minmax(0,24rem)]">
        <div className="space-y-6">
          <DealDetails deal={record} />
        </div>

        <div className="min-w-0 space-y-6">
          <RecordCard title="Activity" className="pb-5">
            <GraphTimeline resource="deals" id={record.id} />
          </RecordCard>
        </div>

        {/* Two across under the others when there is no room for a third column. */}
        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:col-span-2 lg:grid-cols-2 xl:col-span-1 xl:grid-cols-1">
          <RecordCard title="Parties" count={record.parties?.length ?? 0}>
            {(record.parties ?? []).length ? (
              <ul>
                {record.parties.map((party) => (
                  <RecordRow
                    key={`${party.contact_id}-${party.role}`}
                    primary={
                      <>
                        <Link
                          className={RECORD_LINK}
                          to={`/contacts/${party.contact_id}/show`}
                        >
                          {party.first_name} {party.last_name}
                        </Link>{" "}
                        <span className={MUTED}>
                          — {roleText(party.role)}
                          {party.is_primary ? " (primary)" : ""}
                        </span>
                      </>
                    }
                    trailing={
                      canEditParties ? (
                        <EndPartyButton
                          partyId={party.id}
                          label={`${party.first_name} ${party.last_name} as ${roleText(party.role)}`}
                        />
                      ) : null
                    }
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
            {canEditParties ? <AddPartyForm dealId={record.id} /> : null}
          </RecordCard>

          <TeamCard assignments={record.assignments} />

          <ExternalIdsCard ids={record.external_ids} />

          <LoansCard loans={record.loans ?? []} />

          <LoanProgressCard
            progress={record.loan_progress ?? []}
            loans={record.loans ?? []}
          />

          <RelatedDeals dealId={record.id} links={record.links ?? []} />
        </div>
      </div>
    </div>
  );
}
